import AuthService from './AuthService';

class KbaseService {
    constructor() {
        const rawBaseUrl = process.env.REACT_APP_CHATBOT_API_URL || process.env.REACT_APP_API_URL || '';
        this.apiBaseUrl = String(rawBaseUrl).replace(/\/+$/, '');
    }

    getAuthHeaders(extraHeaders) {
        const token = AuthService.getAccessToken();
        const authHeader = token ? { Authorization: `Bearer ${token}` } : {};
        return {
            ...authHeader,
            ...(extraHeaders || {}),
        };
    }

    sanitizeCreatePayload(payload) {
        const body = { ...(payload || {}) };
        // Let the backend generate the ID.
        delete body.kbaseId;
        // Server-managed fields should not be sent from UI.
        delete body.createdAt;
        return body;
    }

    sanitizeUpdatePayload(kbaseId, payload) {
        const body = { ...(payload || {}) };
        // Ensure payload matches the URL param to satisfy backend validation.
        body.kbaseId = kbaseId;
        // Server-managed fields should not be sent from UI.
        delete body.createdAt;
        return body;
    }

    async getKbases(clientId) {
        if (!clientId) throw new Error('clientId is required');

        const url = `${this.apiBaseUrl}/admin/clients/${encodeURIComponent(clientId)}/kbases`;
        const response = await fetch(url, {
            method: 'GET',
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return Array.isArray(data) ? data : [];
    }

    async getCustomerKbases() {
        const loginAs = AuthService.getLoginAs();
        const loginAsParam = loginAs ? `?loginAs=${loginAs}` : '';
        const url = `${this.apiBaseUrl}/private/kbases${loginAsParam}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return Array.isArray(data) ? data : [];
    }

    async getKbaseDocuments(kbaseId, options) {
        if (!kbaseId) throw new Error('kbaseId is required');

        const urlBase = `${this.apiBaseUrl}/private/kbases/${encodeURIComponent(kbaseId)}/documents`;
        const folder = String(options?.folder ?? '').trim();
        const qs = new URLSearchParams();
        if (folder) qs.set('folder', folder);
        const loginAs = AuthService.getLoginAs();
        if (loginAs) qs.set('loginAs', loginAs);
        const url = qs.toString() ? `${urlBase}?${qs.toString()}` : urlBase;
        const response = await fetch(url, {
            method: 'GET',
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return Array.isArray(data) ? data : [];
    }

    async getKbaseFeedback(kbaseId, options) {
        if (!kbaseId) throw new Error('kbaseId is required');
        const qs = new URLSearchParams();
        if (options?.status && options.status !== 'all') qs.set('feedbackStatus', options.status);
        const loginAs = AuthService.getLoginAs();
        if (loginAs) qs.set('loginAs', loginAs);
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        const response = await fetch(`${this.apiBaseUrl}/private/kbases/${encodeURIComponent(kbaseId)}/feedback${suffix}`, { headers: this.getAuthHeaders() });
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        const data = await response.json();
        return Array.isArray(data) ? data : [];
    }

    async updateKbaseFeedback(kbaseId, assessmentId, payload) {
        if (!kbaseId || !assessmentId) throw new Error('kbaseId and assessmentId are required');
        const qs = new URLSearchParams();
        const loginAs = AuthService.getLoginAs();
        if (loginAs) qs.set('loginAs', loginAs);
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        const response = await fetch(`${this.apiBaseUrl}/private/kbases/${encodeURIComponent(kbaseId)}/feedback/${encodeURIComponent(assessmentId)}${suffix}`, {
            method: 'PATCH', headers: this.getAuthHeaders({ 'Content-Type': 'application/json' }), body: JSON.stringify(payload || {}),
        });
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        return await response.json();
    }

    async saveKbaseFeedbackDraft(kbaseId, assessmentId, payload) {
        const qs = new URLSearchParams();
        const loginAs = AuthService.getLoginAs();
        if (loginAs) qs.set('loginAs', loginAs);
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        const response = await fetch(`${this.apiBaseUrl}/private/kbases/${encodeURIComponent(kbaseId)}/feedback/${encodeURIComponent(assessmentId)}/draft${suffix}`, {
            method: 'PUT', headers: this.getAuthHeaders({ 'Content-Type': 'application/json' }), body: JSON.stringify(payload),
        });
        if (!response.ok) throw new Error((await response.json().catch(() => null))?.detail || `HTTP error! Status: ${response.status}`);
        return await response.json();
    }

    async publishKbaseFeedbackDraft(kbaseId, assessmentId, payload) {
        const qs = new URLSearchParams();
        const loginAs = AuthService.getLoginAs();
        if (loginAs) qs.set('loginAs', loginAs);
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        const response = await fetch(`${this.apiBaseUrl}/private/kbases/${encodeURIComponent(kbaseId)}/feedback/${encodeURIComponent(assessmentId)}/publish${suffix}`, {
            method: 'POST', headers: this.getAuthHeaders({ 'Content-Type': 'application/json' }), body: JSON.stringify(payload),
        });
        if (!response.ok) throw new Error((await response.json().catch(() => null))?.detail || `HTTP error! Status: ${response.status}`);
        return await response.json();
    }

    async unpublishKbaseFeedbackDraft(kbaseId, assessmentId, payload) {
        const qs = new URLSearchParams();
        const loginAs = AuthService.getLoginAs();
        if (loginAs) qs.set('loginAs', loginAs);
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        const response = await fetch(`${this.apiBaseUrl}/private/kbases/${encodeURIComponent(kbaseId)}/feedback/${encodeURIComponent(assessmentId)}/unpublish${suffix}`, {
            method: 'POST', headers: this.getAuthHeaders({ 'Content-Type': 'application/json' }), body: JSON.stringify(payload || {}),
        });
        if (!response.ok) throw new Error((await response.json().catch(() => null))?.detail || `HTTP error! Status: ${response.status}`);
        return await response.json();
    }

    async createKbaseDocument(kbaseId, payload, options) {
        if (!kbaseId) throw new Error('kbaseId is required');

        const urlBase = `${this.apiBaseUrl}/private/kbases/${encodeURIComponent(kbaseId)}/documents`;
        const folder = String(options?.folder ?? '').trim();
        const qs = new URLSearchParams();
        if (folder) qs.set('folder', folder);
        const url = qs.toString() ? `${urlBase}?${qs.toString()}` : urlBase;

        const body = { ...(payload || {}) };
        if (folder && !String(body.folder ?? '').trim()) {
            body.folder = folder;
        }
        const response = await fetch(url, {
            method: 'POST',
            headers: this.getAuthHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(body),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const text = await response.text();
        try {
            return text ? JSON.parse(text) : { ok: true };
        } catch {
            return { ok: true };
        }
    }

    async getKbase(clientId, kbaseId) {
        if (!clientId) throw new Error('clientId is required');
        if (!kbaseId) throw new Error('kbaseId is required');

        const url = `${this.apiBaseUrl}/admin/clients/${encodeURIComponent(clientId)}/kbases/${encodeURIComponent(kbaseId)}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        return await response.json();
    }

    async createKbase(clientId, payload) {
        if (!clientId) throw new Error('clientId is required');

        const url = `${this.apiBaseUrl}/admin/clients/${encodeURIComponent(clientId)}/kbases`;
        const body = this.sanitizeCreatePayload(payload);
        const response = await fetch(url, {
            method: 'POST',
            headers: this.getAuthHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(body),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        return await response.json();
    }

    async updateKbase(clientId, kbaseId, payload) {
        if (!clientId) throw new Error('clientId is required');
        if (!kbaseId) throw new Error('kbaseId is required');

        const url = `${this.apiBaseUrl}/admin/clients/${encodeURIComponent(clientId)}/kbases/${encodeURIComponent(kbaseId)}`;
        const body = this.sanitizeUpdatePayload(kbaseId, payload);
        const response = await fetch(url, {
            method: 'PUT',
            headers: this.getAuthHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(body),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        return await response.json();
    }

    async deleteKbase(clientId, kbaseId) {
        if (!clientId) throw new Error('clientId is required');
        if (!kbaseId) throw new Error('kbaseId is required');

        const url = `${this.apiBaseUrl}/admin/clients/${encodeURIComponent(clientId)}/kbases/${encodeURIComponent(kbaseId)}`;
        const response = await fetch(url, {
            method: 'DELETE',
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const text = await response.text();
        try {
            return text ? JSON.parse(text) : { ok: true };
        } catch {
            return { ok: true };
        }
    }

    async deleteKbaseDocument(kbaseId, documentId) {
        if (!kbaseId) throw new Error('kbaseId is required');
        if (!documentId) throw new Error('documentId is required');

        const url = `${this.apiBaseUrl}/private/kbases/${encodeURIComponent(kbaseId)}/documents/${encodeURIComponent(documentId)}`;
        const response = await fetch(url, {
            method: 'DELETE',
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const text = await response.text();
        try {
            return text ? JSON.parse(text) : { ok: true };
        } catch {
            return { ok: true };
        }
    }

    async updateKbaseDocument(kbaseId, documentId, payload) {
        if (!kbaseId) throw new Error('kbaseId is required');
        if (!documentId) throw new Error('documentId is required');

        const url = `${this.apiBaseUrl}/private/kbases/${encodeURIComponent(kbaseId)}/documents/${encodeURIComponent(documentId)}`;
        const body = {
            ...(payload || {}),
            documentId,
        };
        const response = await fetch(url, {
            method: 'PUT',
            headers: this.getAuthHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(body),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const text = await response.text();
        try {
            return text ? JSON.parse(text) : { ok: true };
        } catch {
            return { ok: true };
        }
    }

    async moveKbaseDocument(kbaseId, documentId, folder) {
        return this.updateKbaseDocument(kbaseId, documentId, {
            folder: String(folder ?? '').trim(),
        });
    }

    async getKbaseDocumentPageImage(kbaseId, documentId, pageNum) {
        if (!kbaseId) throw new Error('kbaseId is required');
        if (!documentId) throw new Error('documentId is required');
        if (pageNum == null || String(pageNum).trim() === '') throw new Error('pageNum is required');

        const pageInt = Number(pageNum);
        if (!Number.isFinite(pageInt) || pageInt < 0) throw new Error('pageNum must be a non-negative number');

        const loginAs = AuthService.getLoginAs();
        const loginAsParam = loginAs ? `?loginAs=${encodeURIComponent(loginAs)}` : '';
        const url = `${this.apiBaseUrl}/private/kbases/${encodeURIComponent(kbaseId)}/${encodeURIComponent(documentId)}/${encodeURIComponent(String(Math.floor(pageInt)))}${loginAsParam}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const contentType = String(response.headers.get('content-type') || '').toLowerCase();
        // If backend returns the image directly, just return it.
        if (contentType.startsWith('image/')) {
            return await response.blob();
        }

        // Legacy method intentionally does not download from presigned URLs to avoid
        // loading image bytes into JS memory. Use getKbaseDocumentPageImageSrc instead.
        throw new Error('Preview endpoint returned a URL payload; use getKbaseDocumentPageImageSrc() to display it without downloading');
    }

    async getKbaseDocumentPageImageSrc(kbaseId, documentId, pageNum) {
        if (!kbaseId) throw new Error('kbaseId is required');
        if (!documentId) throw new Error('documentId is required');
        if (pageNum == null || String(pageNum).trim() === '') throw new Error('pageNum is required');

        const pageInt = Number(pageNum);
        if (!Number.isFinite(pageInt) || pageInt < 0) throw new Error('pageNum must be a non-negative number');

        const loginAs = AuthService.getLoginAs();
        const loginAsParam = loginAs ? `?loginAs=${encodeURIComponent(loginAs)}` : '';
        const url = `${this.apiBaseUrl}/private/kbases/${encodeURIComponent(kbaseId)}/${encodeURIComponent(documentId)}/${encodeURIComponent(String(Math.floor(pageInt)))}${loginAsParam}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: this.getAuthHeaders({ Accept: 'image/*' }),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const previewBlob = await response.blob();
        if (!String(previewBlob.type || '').toLowerCase().startsWith('image/') || previewBlob.size === 0) {
            throw new Error('Preview endpoint did not return an image');
        }
        return { src: URL.createObjectURL(previewBlob), revoke: true };
    }

    async getKbasePdfDocumentSrc(kbaseId, documentId) {
        if (!kbaseId) throw new Error('kbaseId is required');
        if (!documentId) throw new Error('documentId is required');

        const loginAs = AuthService.getLoginAs();
        const loginAsParam = loginAs ? `?loginAs=${encodeURIComponent(loginAs)}` : '';
        const url = `${this.apiBaseUrl}/private/kbases/${encodeURIComponent(kbaseId)}/documents/${encodeURIComponent(documentId)}/pdf${loginAsParam}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: this.getAuthHeaders({ Accept: 'application/pdf' }),
        });

        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        const pdfBlob = await response.blob();
        if (!String(pdfBlob.type || '').toLowerCase().startsWith('application/pdf') || pdfBlob.size === 0) {
            throw new Error('Document endpoint did not return a PDF');
        }
        return URL.createObjectURL(pdfBlob);
    }

    async queryKbase(kbaseId, query, top) {
        if (!kbaseId) throw new Error('kbaseId is required');
        if (!query) throw new Error('query is required');

        const params = new URLSearchParams();
        params.set('query', String(query));
        if (top != null && String(top).trim() !== '') params.set('top', String(top));

        const url = `${this.apiBaseUrl}/private/kbases/${encodeURIComponent(kbaseId)}/vectors/search?${params.toString()}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return Array.isArray(data) ? data : [];
    }
}

const kbaseService = new KbaseService();
export { KbaseService };
export default kbaseService;
